import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { scrollableTableSizing } from '../styles/scrollableTable';
import { FiPackage, FiSearch, FiFilter, FiRefreshCw, FiChevronDown, FiChevronUp, FiPrinter, FiDownload, FiXCircle, FiTrash2 } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { supabase } from '../../config/supabase';
import { useLanguage } from '../../context/LanguageContext';

const isSystemInternationalNote = (note) => {
  const normalized = String(note || '').trim().toLowerCase();
  return normalized.startsWith('international order to ') && normalized.includes('requires manual shipment setup');
};

const sanitizeOrderItemNote = (note) => (isSystemInternationalNote(note) ? null : note);

const Container = styled.div`
  padding: 32px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ControlsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 18px;
`;

const OrderTabs = styled.div`
  display: inline-flex;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 6px;
`;

const OrderTabButton = styled.button`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.$active ? '#86efac' : 'transparent'};
  background: ${props => props.$active ? '#ecfdf3' : 'transparent'};
  color: ${props => props.$active ? '#166534' : '#4b5563'};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
`;

const FiltersPanel = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #d9fbe8;
  background: linear-gradient(180deg, #f8fffb 0%, #f5fff9 100%);
`;

const FilterTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #14532d;
  padding-right: 8px;
`;

const FilterSelect = styled.select`
  min-width: 190px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.2);
  }
`;

const FilterDateInput = styled.input`
  min-width: 170px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.2);
  }
`;

const FilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid #c7f9df;
  background: #ecfdf3;
  color: #166534;
  font-size: 12px;
  font-weight: 600;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 16px;
  gap: 8px;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;
  width: 300px;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
    
    &:hover {
      background: #9ca3af;
    }
  }
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: separate;
  border-spacing: 0;
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #374151;
  background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: sticky;
  top: 0;
  z-index: 10;
  
  &:first-child {
    padding-left: 24px;
    border-top-left-radius: 8px;
  }
  
  &:last-child {
    padding-right: 24px;
    border-top-right-radius: 8px;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1f2937;
  border-bottom: 1px solid #f3f4f6;
  background: white;
  vertical-align: middle;
  
  &:first-child {
    padding-left: 24px;
  }
  
  &:last-child {
    padding-right: 24px;
  }
`;

const TableRow = styled.tr`
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb !important;
    transform: scale(1.001);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    
    ${TableCell} {
      background: #f9fafb;
    }
  }
  
  &:nth-child(even) {
    ${TableCell} {
      background: #fafbfc;
    }
  }

  &:last-child ${TableCell} {
    border-bottom: none;
  }
  
  /* Add subtle left border accent on hover */
  &:hover ${TableCell}:first-child {
    border-left: 3px solid #5efba9;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  
  background: ${props => {
    if (props.status === 'completed') return 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)';
    if (props.status === 'pending') return 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)';
    if (props.status === 'processing') return 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)';
    if (props.status === 'canceled') return 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
    return 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)';
  }};
  
  color: ${props => {
    if (props.status === 'completed') return '#065f46';
    if (props.status === 'pending') return '#92400e';
    if (props.status === 'processing') return '#1e40af';
    if (props.status === 'canceled') return '#991b1b';
    return '#065f46';
  }};
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: ${props => props.status === 'processing' ? 'pulse 2s infinite' : 'none'};
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 32px;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const ExpandableRow = styled(TableRow)`
  background: #f9fafb;
`;

const ExpandedContent = styled.div`
  padding: 24px;
  background: linear-gradient(to bottom, #fafbfc 0%, #ffffff 100%);
  border-top: 2px solid #f3f4f6;
  animation: slideDown 0.3s ease;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ExpandedSection = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid #f3f4f6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '';
    width: 3px;
    height: 16px;
    background: linear-gradient(to bottom, #5efba9, #4de89e);
    border-radius: 2px;
  }
`;

const PrintButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 16px;

  &:hover {
    background: #4de89e;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const WhatsAppButton = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #25D366;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 16px;
  text-decoration: none;
  margin-left: 8px;

  &:hover {
    background: #20BA5A;
    transform: translateY(-1px);
    color: white;
  }

  &:active {
    transform: translateY(0);
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const CancelOrderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #ef4444;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 16px;

  &:hover:not(:disabled) {
    background: #dc2626;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #fca5a5;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const DeleteOrderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #111827;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 16px;

  &:hover:not(:disabled) {
    background: #000000;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #9ca3af;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
    transform: translateX(4px);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  gap: 12px;
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #f3f4f6;
  transition: all 0.2s ease;
  
  ${ItemRow}:hover & {
    border-color: #5efba9;
    transform: scale(1.05);
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemName = styled.div`
  font-weight: 500;
  color: #1f2937;
`;

const ItemMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 24px auto;
  padding: 12px 32px;
  background: #5efba9;
  border: 1px solid #5efba9;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4dd699;
    border-color: #4dd699;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusSelect = styled.select`
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  border: none;
  background: ${props => {
    if (props.value === 'completed') return '#d1fae5';
    if (props.value === 'pending') return '#fef3c7';
    if (props.value === 'processing') return '#dbeafe';
    if (props.value === 'canceled') return '#fee2e2';
    return '#e5e7eb';
  }};
  color: ${props => {
    if (props.value === 'completed') return '#065f46';
    if (props.value === 'pending') return '#92400e';
    if (props.value === 'processing') return '#1e40af';
    if (props.value === 'canceled') return '#991b1b';
    return '#374151';
  }};
  cursor: pointer;
  outline: none;

  &:focus {
    outline: 2px solid #5efba9;
    outline-offset: 2px;
  }
`;

const normalizeShipmentStatus = (shipmentStatusName) => {
  if (!shipmentStatusName || shipmentStatusName === '') return 'Pending';
  const status = String(shipmentStatusName).toLowerCase().trim();
  if (status.includes('shipping request') || status.includes('طلب شحن')) return 'Shipping Request';
  if (status.includes('received in the store') || status.includes('الاستلام') || status.includes('المخزن')) return 'Received in the store';
  if (
    status.includes('out to deliver') ||
    status.includes('out for delivery') ||
    status.includes('جاري التوصيل') ||
    status.includes('قيد التوصيل')
  ) return 'Out for Delivery';
  if (status.includes('delivered') || status.includes('تم التسليم')) return 'Delivered';
  if (status.includes('refund')) return 'Refunded';
  return shipmentStatusName;
};

const getStatusTone = (statusText) => {
  const status = String(statusText || '').toLowerCase().trim();
  if (!status) return 'pending';
  if (status.includes('cancel')) return 'canceled';
  if (status.includes('deliver') || status.includes('complete')) return 'completed';
  if (
    status.includes('processing') ||
    status.includes('shipping request') ||
    status.includes('received in the store') ||
    status.includes('out to deliver') ||
    status.includes('out for delivery')
  ) return 'processing';
  return 'pending';
};

function AdminOrders() {
  const { isArabic } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [displayedCount, setDisplayedCount] = useState(20);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const [customFieldsMap, setCustomFieldsMap] = useState({}); // key_name -> {label, label_ar}

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchEmailsForProfiles = async (profileIds) => {
    const uniqueIds = [...new Set((profileIds || []).filter(Boolean))];
    const emailMap = new Map();
    if (uniqueIds.length === 0) return emailMap;
    const chunkSize = 20;
    const chunks = [];
    for (let i = 0; i < uniqueIds.length; i += chunkSize) {
      chunks.push(uniqueIds.slice(i, i + chunkSize));
    }
    await Promise.all(
      chunks.map(async (chunk) => {
        await Promise.all(
          chunk.map(async (profileId) => {
            try {
              const { data, error } = await supabase.rpc('get_user_email', { user_id: profileId });
              if (!error && typeof data === 'string' && data.includes('@')) {
                emailMap.set(profileId, data.trim());
              }
            } catch (_err) {
              // keep fallback behavior
            }
          })
        );
      })
    );
    return emailMap;
  };

  const getCustomerEmail = (order) => (
    order.user_email ||
    order.profile?.email ||
    order.profiles?.email ||
    order.shipping_snapshot?.email ||
    'N/A'
  );

  const fetchOrders = async () => {
    try {
      setLoading(true);
      console.log('Fetching orders from orders table...');

      // Fetch ALL columns explicitly to ensure we get everything (only paid orders)
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          created_at,
          updated_at,
          profile_id,
          cart_id,
          currency,
          subtotal,
          shipping_fee,
          discount_total,
          tax_total,
          grand_total,
          status,
          payment_status,
          fulfillment_status,
          payment_method,
          payment_reference,
          shipping_information_id,
          shipping_snapshot,
          customer_note,
          admin_note,
          order_items (
            *,
            products (
              id,
              name,
              images,
              sku,
              category_id,
              subcategory_id,
              category:categories(
                id,
                name_en,
                name_ar
              ),
              subcategory:subcategories(
                id,
                category_id,
                name_en,
                name_ar
              )
            ),
            brand:brands (
              id,
              name
            )
          )
        `)
        .eq('payment_status', 'paid')
        .order('created_at', { ascending: false });

      console.log('Orders query response - data:', ordersData);
      console.log('Orders query response - error:', ordersError);

      if (ordersError) {
        console.error('Orders query error:', ordersError);
        throw ordersError;
      }

      if (!ordersData) {
        console.log('No orders data returned');
        setOrders([]);
        return;
      }

      const profileIds = [...new Set((ordersData || []).map(order => order.profile_id).filter(Boolean))];
      const profileEmailMap = await fetchEmailsForProfiles(profileIds);

      // Transform the data to match our expected structure
      // Filter out orders with no valid brands or deleted brands
      const transformedOrders = ordersData
        .map(order => {
          console.log('Processing order:', order.id, 'with fields:', Object.keys(order));

          // Flatten the product relationship in order_items
          const transformedItems = (order.order_items || []).map(item => ({
            ...item,
            note: sanitizeOrderItemNote(item.note),
            product: item.products || null
          }));

          // Get unique brands from order items (only valid brands that exist)
          const brands = new Set();
          (order.order_items || []).forEach(item => {
            // Only add brand if it exists and has a name (not deleted)
            if (item.brand?.id && item.brand?.name) {
              brands.add(item.brand.name);
            }
          });
          const brandNames = Array.from(brands);

          const categoryMap = new Map();
          const subcategoryMap = new Map();
          transformedItems.forEach(item => {
            const product = item.product || {};
            const categoryId = product.category_id ? String(product.category_id) : null;
            const subcategoryId = product.subcategory_id ? String(product.subcategory_id) : null;
            const categoryName = isArabic
              ? (product.category?.name_ar || product.category?.name_en || product.category?.name)
              : (product.category?.name_en || product.category?.name || product.category?.name_ar);
            const subcategoryName = isArabic
              ? (product.subcategory?.name_ar || product.subcategory?.name_en || product.subcategory?.name)
              : (product.subcategory?.name_en || product.subcategory?.name || product.subcategory?.name_ar);
            if (categoryId && categoryName) {
              categoryMap.set(categoryId, { id: categoryId, name: categoryName });
            }
            if (subcategoryId && subcategoryName) {
              subcategoryMap.set(subcategoryId, {
                id: subcategoryId,
                name: subcategoryName,
                categoryId: product.subcategory?.category_id ? String(product.subcategory.category_id) : categoryId
              });
            }
          });

          const shipmentStatus = transformedItems
            .map(item => item.shipment_status_name)
            .find(Boolean) || order.status || 'Pending';

          return {
            ...order,
            order_items: transformedItems,
            brand_names: brandNames,
            primary_brand: brandNames[0] || 'N/A',
            user_email: profileEmailMap.get(order.profile_id) || null,
            shipment_status_name: shipmentStatus,
            category_entries: Array.from(categoryMap.values()),
            subcategory_entries: Array.from(subcategoryMap.values())
          };
        })
        // Filter out orders with no valid brands (N/A or deleted brands)
        .filter(order => {
          const hasValidBrand = order.brand_names && order.brand_names.length > 0 && order.primary_brand !== 'N/A';
          if (!hasValidBrand) {
            console.log(`Filtering out order ${order.id} - no valid brands found`);
          }
          return hasValidBrand;
        });

      // Fetch custom fields for all products in orders
      const customFieldsMap = {};
      const allProductIds = new Set();
      transformedOrders.forEach(order => {
        (order.order_items || []).forEach(item => {
          if (item.product?.id) {
            allProductIds.add(item.product.id);
          }
        });
      });
      
      const productIds = Array.from(allProductIds);
      if (productIds.length > 0) {
        const { data: customFieldsData } = await supabase
          .from('product_custom_fields')
          .select('key_name, label, label_ar')
          .in('product_id', productIds);
        
        if (customFieldsData) {
          customFieldsData.forEach(field => {
            customFieldsMap[field.key_name] = {
              label: field.label,
              label_ar: field.label_ar
            };
          });
        }
      }
      
      setCustomFieldsMap(customFieldsMap);

      console.log('Transformed orders:', transformedOrders);
      console.log('First order sample:', transformedOrders[0]);
      setOrders(transformedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert(`Error fetching orders: ${error.message}`);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const exportToExcel = () => {
    if (filteredOrders.length === 0) {
      alert('No orders to export');
      return;
    }

    // Prepare data for Excel
    const excelData = filteredOrders.map(order => {
      const orderDate = new Date(order.created_at).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      // Get order items summary
      const itemsSummary = order.order_items && order.order_items.length > 0
        ? order.order_items.map(item => `${item.product_name} (Qty: ${item.quantity})`).join('; ')
        : 'N/A';

      return {
        'Order ID': order.id.substring(0, 8).toUpperCase(),
        'Brand': order.brand_names && order.brand_names.length > 0 ? order.brand_names.join('; ') : 'N/A',
        'Categories': order.category_entries?.map(entry => entry.name).join('; ') || 'N/A',
        'Subcategories': order.subcategory_entries?.map(entry => entry.name).join('; ') || 'N/A',
        'Date': orderDate,
        'Customer Name': order.shipping_snapshot?.full_name || 'N/A',
        'Customer Phone': order.shipping_snapshot?.phone_number || 'N/A',
        'Customer Email': getCustomerEmail(order),
        'Shipping Address': order.shipping_snapshot 
          ? `${order.shipping_snapshot.area || ''}, ${order.shipping_snapshot.city || ''}, ${order.shipping_snapshot.governorate || ''}`
          : 'N/A',
        'Order Items': itemsSummary,
        'Subtotal': order.subtotal?.toFixed(2) || '0.00',
        'Shipping Fee': order.shipping_fee?.toFixed(2) || '0.00',
        'Discount': order.discount_total?.toFixed(2) || '0.00',
        'Payment Fee': order.payment_fee?.toFixed(2) || '0.00',
        'Grand Total': order.grand_total?.toFixed(2) || '0.00',
        'Currency': order.currency || 'OMR',
        'Order Status': normalizeShipmentStatus(order.shipment_status_name),
        'Payment Status': order.payment_status || 'N/A'
      };
    });

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');

    // Set column widths
    const columnWidths = [
      { wch: 12 }, // Order ID
      { wch: 20 }, // Brand
      { wch: 20 }, // Categories
      { wch: 20 }, // Subcategories
      { wch: 20 }, // Date
      { wch: 20 }, // Customer Name
      { wch: 15 }, // Phone
      { wch: 25 }, // Email
      { wch: 30 }, // Address
      { wch: 40 }, // Items
      { wch: 12 }, // Subtotal
      { wch: 12 }, // Shipping
      { wch: 12 }, // Discount
      { wch: 12 }, // Payment Fee
      { wch: 12 }, // Grand Total
      { wch: 8 },  // Currency
      { wch: 15 }, // Order Status
      { wch: 15 } // Payment Status
    ];
    worksheet['!cols'] = columnWidths;

    // Generate Excel file and download
    const fileName = `orders_${new Date().toISOString().split('T')[0]}${selectedBrand || selectedCategory || selectedSubcategory || startDate || endDate ? '_filtered' : ''}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const getWhatsAppUrl = (phoneNumber) => {
    if (!phoneNumber) return null;
    
    // Remove all non-digit characters except +
    let phone = phoneNumber.replace(/[^\d+]/g, '');
    
    // If phone doesn't start with +, assume it's a local number
    if (!phone.startsWith('+')) {
      // Remove leading zeros
      phone = phone.replace(/^0+/, '');
      // Add Oman country code if not present
      if (!phone.startsWith('968')) {
        phone = '968' + phone;
      }
      phone = '+' + phone;
    }
    
    // WhatsApp URL format: https://wa.me/PHONENUMBER (with country code, no +)
    const whatsappNumber = phone.replace(/^\+/, '');
    return `https://wa.me/${whatsappNumber}`;
  };

  const printReceipt = (order) => {
    const printWindow = window.open('', '_blank');
    const orderDate = new Date(order.created_at).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - Order ${order.id.substring(0, 8).toUpperCase()}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
              color: #1f2937;
            }
            .header {
              margin-bottom: 30px;
              border-bottom: 2px solid #5efba9;
              padding-bottom: 20px;
            }
            .brand-row {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 16px;
            }
            .brand-logo {
              width: 88px;
              height: auto;
              object-fit: contain;
            }
            .header-text {
              text-align: right;
              flex: 1;
            }
            .header-text h1 {
              font-size: 24px;
              color: #1f2937;
              margin-bottom: 6px;
            }
            .header-text p {
              color: #6b7280;
              font-size: 14px;
              margin-bottom: 4px;
            }
            .order-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 30px;
            }
            .info-section h3 {
              font-size: 14px;
              color: #6b7280;
              margin-bottom: 8px;
              text-transform: uppercase;
            }
            .info-section p {
              font-size: 16px;
              color: #1f2937;
              font-weight: 500;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 30px;
            }
            .items-table th {
              background: #f3f4f6;
              padding: 12px;
              text-align: left;
              font-size: 12px;
              color: #6b7280;
              text-transform: uppercase;
              border-bottom: 2px solid #e5e7eb;
            }
            .items-table td {
              padding: 12px;
              border-bottom: 1px solid #e5e7eb;
            }
            .items-table tr:last-child td {
              border-bottom: none;
            }
            .total-section {
              margin-top: 20px;
              padding-top: 20px;
              border-top: 2px solid #e5e7eb;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
              font-size: 14px;
            }
            .total-row.grand-total {
              font-size: 20px;
              font-weight: 700;
              color: #1f2937;
              margin-top: 12px;
              padding-top: 12px;
              border-top: 2px solid #5efba9;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              color: #6b7280;
              font-size: 12px;
              border-top: 1px solid #e5e7eb;
              padding-top: 20px;
            }
            @media print {
              body { padding: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="brand-row">
              <img src="${window.location.origin}/logo-arabic.svg" alt="WASL Logo" class="brand-logo" />
              <div class="header-text">
                <h1>Order Receipt | إيصال الطلب</h1>
                <p>Easier Shopping, Faster Delivery | تسوق أسهل، توصيل أسرع</p>
              </div>
            </div>
          </div>
          
          <div class="order-info">
            <div class="info-section">
              <h3>Order Number</h3>
              <p>${order.id.substring(0, 8).toUpperCase()}</p>
            </div>
            <div class="info-section">
              <h3>Date</h3>
              <p>${orderDate}</p>
            </div>
          </div>

          ${order.shipping_snapshot ? `
            <div class="info-section" style="margin-bottom: 30px;">
              <h3>Shipping Information</h3>
              <p><strong>Name:</strong> ${order.shipping_snapshot.full_name || 'N/A'}</p>
              <p><strong>Phone:</strong> ${order.shipping_snapshot.phone_number || 'N/A'}</p>
              <p><strong>Email:</strong> ${getCustomerEmail(order)}</p>
              <p><strong>Address:</strong> ${order.shipping_snapshot.area || ''}, ${order.shipping_snapshot.city || ''}, ${order.shipping_snapshot.governorate || ''}</p>
            </div>
          ` : ''}

          <table class="items-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${order.order_items && order.order_items.map(item => `
                <tr>
                  <td><strong>${item.product_name}</strong></td>
                  <td>${item.quantity}</td>
                  <td>${item.unit_price.toFixed(2)} ${order.currency}</td>
                  <td><strong>${item.line_total.toFixed(2)} ${order.currency}</strong></td>
                </tr>
              `).join('') || ''}
            </tbody>
          </table>

          <div class="total-section">
            <div class="total-row">
              <span>Subtotal:</span>
              <span>${order.subtotal.toFixed(2)} ${order.currency}</span>
            </div>
            ${order.shipping_fee > 0 ? `
              <div class="total-row">
                <span>Shipping:</span>
                <span>${order.shipping_fee.toFixed(2)} ${order.currency}</span>
              </div>
            ` : ''}
            ${order.discount_total > 0 ? `
              <div class="total-row">
                <span>Discount:</span>
                <span>-${order.discount_total.toFixed(2)} ${order.currency}</span>
              </div>
            ` : ''}
            ${order.payment_fee > 0 ? `
              <div class="total-row">
                <span>Payment Fee:</span>
                <span>${order.payment_fee.toFixed(2)} ${order.currency}</span>
              </div>
            ` : ''}
            <div class="total-row grand-total">
              <span>Grand Total:</span>
              <span>${order.grand_total.toFixed(2)} ${order.currency}</span>
            </div>
          </div>

          ${order.customer_note ? `
            <div style="margin-top: 30px; padding: 15px; background: #f9fafb; border-radius: 8px;">
              <strong>Customer Notes:</strong>
              <p style="margin-top: 8px;">${order.customer_note}</p>
            </div>
          ` : ''}

          <div class="footer">
            <p>Thank you for choosing WASL | شكراً لاختيارك وصل</p>
            <p style="margin-top: 8px;">©2025 WASL. All Rights Reserved</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const filteredOrders = orders.filter(order => {
    const statusValue = String(order.status || '').toLowerCase();
    const isCanceled = statusValue === 'canceled' || statusValue === 'cancelled';
    if (activeTab === 'active' && isCanceled) return false;
    if (activeTab === 'canceled' && !isCanceled) return false;

    const brandMatch = !selectedBrand || (order.brand_names || []).includes(selectedBrand);
    const categoryMatch = !selectedCategory || (order.category_entries || []).some(entry => String(entry.id) === String(selectedCategory));
    const subcategoryMatch = !selectedSubcategory || (order.subcategory_entries || []).some(entry => String(entry.id) === String(selectedSubcategory));
    const orderDate = new Date(order.created_at);
    const startDateMatch = !startDate || orderDate >= new Date(`${startDate}T00:00:00`);
    const endDateMatch = !endDate || orderDate <= new Date(`${endDate}T23:59:59.999`);
    if (!brandMatch || !categoryMatch || !subcategoryMatch || !startDateMatch || !endDateMatch) return false;

    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase().trim();
    const searchDigits = searchTerm.replace(/\D/g, ''); // Extract only digits from search term
    const customerEmail = getCustomerEmail(order);
    
    // If search term has digits, prioritize digit matching
    if (searchDigits.length > 0) {
      const orderIdStr = order.id.toString();
      const orderIdDigits = orderIdStr.replace(/\D/g, ''); // Extract only digits from order ID
      const phoneNumber = order.shipping_snapshot?.phone_number || '';
      const phoneDigits = phoneNumber.replace(/\D/g, ''); // Extract only digits from phone number
      
    return (
        orderIdDigits.includes(searchDigits) || // Match order ID by digits
        phoneDigits.includes(searchDigits) || // Match phone number by digits
      order.id.toLowerCase().includes(searchLower) ||
      customerEmail.toLowerCase().includes(searchLower) ||
      order.shipping_snapshot?.full_name?.toLowerCase().includes(searchLower) ||
        phoneNumber.toLowerCase().includes(searchLower) ||
      String(order.status || '').toLowerCase().includes(searchLower) ||
      String(order.shipment_status_name || '').toLowerCase().includes(searchLower)
    );
    } else {
      // Text-only search
      return (
        order.id.toLowerCase().includes(searchLower) ||
        customerEmail.toLowerCase().includes(searchLower) ||
        order.shipping_snapshot?.full_name?.toLowerCase().includes(searchLower) ||
        String(order.status || '').toLowerCase().includes(searchLower) ||
        String(order.shipment_status_name || '').toLowerCase().includes(searchLower)
      );
    }
  });

  const brandOptions = useMemo(() => {
    const names = new Set();
    orders.forEach(order => (order.brand_names || []).forEach(name => names.add(name)));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const categoryOptions = useMemo(() => {
    const map = new Map();
    orders.forEach(order => {
      (order.category_entries || []).forEach(entry => {
        if (entry?.id && entry?.name) map.set(String(entry.id), entry.name);
      });
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [orders]);

  const subcategoryOptions = useMemo(() => {
    const map = new Map();
    orders.forEach(order => {
      (order.subcategory_entries || []).forEach(entry => {
        const categoryId = entry?.categoryId ? String(entry.categoryId) : '';
        if (selectedCategory && categoryId && categoryId !== String(selectedCategory)) return;
        if (entry?.id && entry?.name) {
          map.set(String(entry.id), { id: String(entry.id), name: entry.name, categoryId });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [orders, selectedCategory]);

  // Reset displayed count when search changes
  useEffect(() => {
    setDisplayedCount(20);
  }, [activeTab, searchTerm, selectedBrand, selectedCategory, selectedSubcategory, startDate, endDate]);

  useEffect(() => {
    if (!selectedCategory) return;
    const isSubcategoryValid = subcategoryOptions.some(option => option.id === String(selectedSubcategory));
    if (!isSubcategoryValid) {
      setSelectedSubcategory('');
    }
  }, [selectedCategory, selectedSubcategory, subcategoryOptions]);

  // Slice to show only displayedCount items
  const displayedOrders = filteredOrders.slice(0, displayedCount);
  const hasMoreOrders = filteredOrders.length > displayedCount;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdatingOrderId(orderId);
      // Get previous status for email context
      const { data: oldOrder } = await supabase
        .from('orders')
        .select('status')
        .eq('id', orderId)
        .single();

      const oldStatus = oldOrder?.status || null;

      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (error) {
        console.error('Error updating order status:', error);
        alert('Failed to update order status. Please try again.');
        return;
      }

      // Trigger order status email if status changed (non-blocking)
      if (oldStatus !== newStatus) {
        try {
          const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://lxprwdplyftigaemzaua.supabase.co';
          const functionUrl = `${supabaseUrl}/functions/v1/send-order-status-email`;
          const { data: { session } } = await supabase.auth.getSession();

          fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session?.access_token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              orderId,
              newStatus,
              oldStatus,
            }),
          }).catch((emailErr) => {
            console.error('Failed to send order status email (non-blocking):', emailErr);
          });
        } catch (emailError) {
          console.error('Error triggering order status email:', emailError);
        }
      }

      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
      return true;
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status. Please try again.');
      return false;
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleCancelOrder = async (orderId) => {
    const confirmed = window.confirm(
      isArabic
        ? 'هل أنت متأكد من إلغاء هذا الطلب؟'
        : 'Are you sure you want to cancel this order?'
    );
    if (!confirmed) return;
    await updateOrderStatus(orderId, 'canceled');
  };

  const handleDeleteCanceledOrder = async (orderId) => {
    const confirmed = window.confirm(
      isArabic
        ? 'هل أنت متأكد من حذف هذا الطلب الملغي نهائيًا؟ لا يمكن التراجع عن هذا الإجراء.'
        : 'Are you sure you want to permanently delete this canceled order? This action cannot be undone.'
    );
    if (!confirmed) return;

    try {
      setDeletingOrderId(orderId);
      const { data, error } = await supabase.rpc('superadmin_delete_canceled_order', {
        p_order_id: orderId
      });

      if (error) {
        console.error('Error deleting canceled order via RPC:', error);
        alert(isArabic ? 'فشل حذف الطلب.' : 'Failed to delete order.');
        return;
      }

      const deleted = data?.success === true;
      if (!deleted) {
        const errorMessage = data?.error || (isArabic ? 'فشل حذف الطلب.' : 'Failed to delete order.');
        alert(errorMessage);
        return;
      }

      setOrders((prevOrders) => prevOrders.filter((order) => order.id !== orderId));
      setExpandedOrders((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    } catch (error) {
      console.error('Error deleting canceled order:', error);
      alert(isArabic ? 'حدث خطأ أثناء حذف الطلب.' : 'An error occurred while deleting the order.');
    } finally {
      setDeletingOrderId(null);
    }
  };

  const canceledOrdersCount = useMemo(() => {
    return orders.filter((order) => {
      const statusValue = String(order.status || '').toLowerCase();
      return statusValue === 'canceled' || statusValue === 'cancelled';
    }).length;
  }, [orders]);

  if (loading) {
    return (
      <Container>
        <EmptyState>Loading orders...</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <ControlsSection>
        <Header>
          <Title>
            <FiPackage /> {isArabic ? 'الطلبات' : 'Orders'}
          </Title>
          <HeaderActions>
            <SearchBar>
              <FiSearch />
              <SearchInput
                type="text"
                placeholder={isArabic ? 'ابحث في الطلبات...' : 'Search orders...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>
            <IconButton onClick={exportToExcel}>
              <FiDownload /> {isArabic ? 'تصدير إكسل' : 'Download Excel'}
            </IconButton>
            <IconButton onClick={fetchOrders}>
              <FiRefreshCw /> {isArabic ? 'تحديث' : 'Refresh'}
            </IconButton>
          </HeaderActions>
        </Header>

        <FiltersPanel>
          <FilterTitle>
            <FiFilter /> {isArabic ? 'فلاتر التصدير والعرض' : 'View & Export Filters'}
          </FilterTitle>
          <FilterChip>
            {isArabic ? `الإجمالي: ${orders.length}` : `Total: ${orders.length}`}
          </FilterChip>
          <FilterChip>
            {isArabic ? `المعروض: ${filteredOrders.length}` : `Showing: ${filteredOrders.length}`}
          </FilterChip>
          <FilterSelect
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
          >
            <option value="">{isArabic ? 'كل الماركات' : 'All Brands'}</option>
            {brandOptions.map((brandName) => (
              <option key={brandName} value={brandName}>{brandName}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setSelectedSubcategory('');
            }}
          >
            <option value="">{isArabic ? 'كل الفئات' : 'All Categories'}</option>
            {categoryOptions.map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={selectedSubcategory}
            onChange={(e) => setSelectedSubcategory(e.target.value)}
          >
            <option value="">{isArabic ? 'كل الفئات الفرعية' : 'All Subcategories'}</option>
            {subcategoryOptions.map((subcategory) => (
              <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>
            ))}
          </FilterSelect>
          <FilterDateInput
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label={isArabic ? 'من تاريخ' : 'From date'}
            title={isArabic ? 'من تاريخ' : 'From date'}
          />
          <FilterDateInput
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label={isArabic ? 'إلى تاريخ' : 'To date'}
            title={isArabic ? 'إلى تاريخ' : 'To date'}
          />
          <IconButton
            onClick={() => {
              setSelectedBrand('');
              setSelectedCategory('');
              setSelectedSubcategory('');
              setStartDate('');
              setEndDate('');
            }}
          >
            {isArabic ? 'مسح الفلاتر' : 'Clear Filters'}
          </IconButton>
        </FiltersPanel>
        <OrderTabs>
          <OrderTabButton
            $active={activeTab === 'active'}
            onClick={() => setActiveTab('active')}
          >
            {isArabic ? 'الطلبات النشطة' : 'Active Orders'} ({orders.length - canceledOrdersCount})
          </OrderTabButton>
          <OrderTabButton
            $active={activeTab === 'canceled'}
            onClick={() => setActiveTab('canceled')}
          >
            {isArabic ? 'الطلبات الملغاة' : 'Canceled Orders'} ({canceledOrdersCount})
          </OrderTabButton>
        </OrderTabs>
      </ControlsSection>

      <Card>
        <TableContainer>
          <Table>
            <thead>
              <TableRow>
                <TableHeaderCell></TableHeaderCell>
                <TableHeaderCell>Order ID</TableHeaderCell>
                <TableHeaderCell>Brand</TableHeaderCell>
                <TableHeaderCell>Customer</TableHeaderCell>
                <TableHeaderCell>Phone</TableHeaderCell>
                <TableHeaderCell>Date</TableHeaderCell>
                <TableHeaderCell>Total</TableHeaderCell>
                  <TableHeaderCell>Order Status</TableHeaderCell>
                <TableHeaderCell>Payment Status</TableHeaderCell>
              </TableRow>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} style={{ textAlign: 'center', padding: '64px' }}>
                    <EmptyIcon>📦</EmptyIcon>
                    <p>No orders found</p>
                  </TableCell>
                </TableRow>
              ) : (
                displayedOrders.map(order => {
                  const isExpanded = expandedOrders.has(order.id);
                  const displayOrderStatus = normalizeShipmentStatus(
                    order.shipment_status_name || order.fulfillment_status || order.status
                  );
                  const displayOrderStatusTone = getStatusTone(displayOrderStatus);
                  return (
                    <React.Fragment key={order.id}>
                      <TableRow>
                        <TableCell>
                          <ExpandButton onClick={() => toggleExpanded(order.id)}>
                            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                          </ExpandButton>
                        </TableCell>
                        <TableCell>
                          <strong>{order.id.substring(0, 8).toUpperCase()}</strong>
                        </TableCell>
                        <TableCell>
                          {order.brand_names && order.brand_names.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              {order.brand_names.map((brandName, idx) => (
                                <span key={idx} style={{ fontSize: '13px' }}>{brandName}</span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>N/A</span>
                          )}
                        </TableCell>
                        <TableCell>{order.shipping_snapshot?.full_name || 'N/A'}</TableCell>
                        <TableCell>
                          {order.shipping_snapshot?.phone_number || 'N/A'}
                        </TableCell>
                        <TableCell>{formatDate(order.created_at)}</TableCell>
                        <TableCell>{order.grand_total.toFixed(2)} {order.currency}</TableCell>
                        <TableCell>
                          <StatusBadge status={displayOrderStatusTone}>{displayOrderStatus || 'Pending'}</StatusBadge>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={order.payment_status}>{order.payment_status}</StatusBadge>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={10}>
                            <ExpandedContent>
                              <ActionButtonsContainer>
                                <PrintButton onClick={() => printReceipt(order)}>
                                  <FiPrinter /> Print Receipt
                                </PrintButton>
                                {String(order.status || '').toLowerCase() !== 'canceled' && String(order.status || '').toLowerCase() !== 'cancelled' && (
                                  <CancelOrderButton
                                    onClick={() => handleCancelOrder(order.id)}
                                    disabled={updatingOrderId === order.id}
                                  >
                                    <FiXCircle />
                                    {updatingOrderId === order.id
                                      ? (isArabic ? 'جارٍ الإلغاء...' : 'Canceling...')
                                      : (isArabic ? 'إلغاء الطلب' : 'Cancel Order')}
                                  </CancelOrderButton>
                                )}
                                {(String(order.status || '').toLowerCase() === 'canceled' || String(order.status || '').toLowerCase() === 'cancelled') && (
                                  <DeleteOrderButton
                                    onClick={() => handleDeleteCanceledOrder(order.id)}
                                    disabled={deletingOrderId === order.id}
                                  >
                                    <FiTrash2 />
                                    {deletingOrderId === order.id
                                      ? (isArabic ? 'جارٍ الحذف...' : 'Deleting...')
                                      : (isArabic ? 'حذف الطلب' : 'Delete Order')}
                                  </DeleteOrderButton>
                                )}
                                {order.shipping_snapshot?.phone_number && getWhatsAppUrl(order.shipping_snapshot.phone_number) && (
                                  <WhatsAppButton
                                    href={getWhatsAppUrl(order.shipping_snapshot.phone_number)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Contact via WhatsApp"
                                  >
                                    <FaWhatsapp /> WhatsApp
                                  </WhatsAppButton>
                                )}
                              </ActionButtonsContainer>
                              <ExpandedSection>
                                <SectionTitle>Order Items ({order.order_items?.length || 0})</SectionTitle>
                                {order.order_items && order.order_items.length > 0 ? (
                                  order.order_items.map(item => {
                                    // Extract additional information from various possible fields
                                    const additionalInfo = (item.attributes && typeof item.attributes === 'object' ? item.attributes : null);
                                    
                                    return (
                                      <div key={item.id} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
                                        <ItemRow>
                                          <ItemDetails>
                                            <ItemImage
                                              src={item.product?.images?.[0] || 'https://via.placeholder.com/48'}
                                              alt={item.product_name}
                                            />
                                            <ItemInfo>
                                              <ItemName>{item.product_name}</ItemName>
                                              <ItemMeta>
                                                SKU: {item.sku || 'N/A'} • Qty: {item.quantity} • {item.unit_price.toFixed(2)} OMR each
                                              </ItemMeta>
                                              {item.note && (
                                                <ItemMeta style={{ marginTop: '4px', fontStyle: 'italic', color: '#4b5563' }}>
                                                  Note: {item.note}
                                                </ItemMeta>
                                              )}
                                            </ItemInfo>
                                          </ItemDetails>
                                          <div style={{ fontWeight: 600 }}>
                                            {item.line_total.toFixed(2)} OMR
                                          </div>
                                        </ItemRow>
                                        
                                        {/* Display Additional Information if available */}
                                        {additionalInfo && Object.keys(additionalInfo).length > 0 && (
                                          <div style={{ marginTop: '12px', paddingLeft: '68px', fontSize: '13px', color: '#374151' }}>
                                            <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1f2937' }}>
                                              {isArabic ? 'معلومات إضافية' : 'Additional Information'}:
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                              {Object.entries(additionalInfo).map(([key, value]) => {
                                                // Skip null, undefined, or empty values
                                                if (value === null || value === undefined || value === '') return null;
                                                
                                                // Get label from custom fields map
                                                const fieldInfo = customFieldsMap[key];
                                                const displayLabel = fieldInfo 
                                                  ? (isArabic && fieldInfo.label_ar ? fieldInfo.label_ar : fieldInfo.label)
                                                  : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                                
                                                // Format the value
                                                let displayValue = value;
                                                if (Array.isArray(value)) {
                                                  displayValue = value.join(', ');
                                                } else if (typeof value === 'object') {
                                                  displayValue = JSON.stringify(value, null, 2);
                                                } else {
                                                  displayValue = String(value);
                                                }
                                                
                                                return (
                                                  <div key={key} style={{ display: 'flex', gap: '8px' }}>
                                                    <span style={{ fontWeight: 500, minWidth: '120px' }}>{displayLabel}:</span>
                                                    <span style={{ color: '#6b7280' }}>{displayValue}</span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })
                                ) : (
                                  <p style={{ color: '#6b7280', fontSize: '14px' }}>No items in this order</p>
                                )}
                              </ExpandedSection>

                              <ExpandedSection>
                                <SectionTitle>Shipping Information</SectionTitle>
                                {order.shipping_snapshot && Object.keys(order.shipping_snapshot).length > 0 ? (
                                  <div style={{ fontSize: '14px', color: '#374151' }}>
                                    <div><strong>Name:</strong> {order.shipping_snapshot.full_name || 'N/A'}</div>
                                    <div><strong>Phone:</strong> {order.shipping_snapshot.phone_number || 'N/A'}</div>
                                    <div><strong>Email:</strong> {getCustomerEmail(order)}</div>
                                    <div><strong>Address:</strong> {order.shipping_snapshot.area}, {order.shipping_snapshot.city}, {order.shipping_snapshot.governorate}</div>
                                    {order.shipping_snapshot.delivery_notes && (
                                      <div style={{ marginTop: '8px', fontStyle: 'italic' }}>Notes: {order.shipping_snapshot.delivery_notes}</div>
                                    )}
                                  </div>
                                ) : (
                                  <p style={{ color: '#6b7280', fontSize: '14px' }}>No shipping information available</p>
                                )}
                              </ExpandedSection>

                              {order.customer_note && (
                                <ExpandedSection>
                                  <SectionTitle>Customer Notes</SectionTitle>
                                  <p style={{ color: '#374151', fontSize: '14px' }}>{order.customer_note}</p>
                                </ExpandedSection>
                              )}

                              {order.admin_note && (
                                <ExpandedSection>
                                  <SectionTitle>Admin Notes</SectionTitle>
                                  <p style={{ color: '#374151', fontSize: '14px' }}>{order.admin_note}</p>
                                </ExpandedSection>
                              )}
                            </ExpandedContent>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        </TableContainer>
      </Card>

      {!loading && hasMoreOrders && (
        <LoadMoreButton onClick={() => setDisplayedCount(prev => prev + 20)}>
          Load More ({filteredOrders.length - displayedCount} remaining)
        </LoadMoreButton>
      )}
    </Container>
  );
}

export default AdminOrders;